/** Combobox de negociação para o formulário de tarefa. */
import { useId } from 'react';
import { SearchIcon, XIcon } from '../../../components/icons';
import Input from '../../../components/ui/Input';
import { cn } from '@/lib/utils';
import type { LeadOption } from '../lib/taskFormPayload';

interface TaskFormLeadSelectProps {
  selectedLeadName: string;
  leadSearchTerm: string;
  isLoadingLeads: boolean;
  filteredLeads: LeadOption[];
  showLeadList: boolean;
  showLeadEmpty: boolean;
  onSearchChange: (value: string) => void;
  onSearchFocus: () => void;
  onSelectLead: (option: LeadOption) => void;
  onClearLead: () => void;
}

export function TaskFormLeadSelect({
  selectedLeadName,
  leadSearchTerm,
  isLoadingLeads,
  filteredLeads,
  showLeadList,
  showLeadEmpty,
  onSearchChange,
  onSearchFocus,
  onSelectLead,
  onClearLead,
}: TaskFormLeadSelectProps) {
  const fieldId = useId();
  const listboxId = `${fieldId}-listbox`;
  const emptyId = `${fieldId}-empty`;
  const searchInputId = `${fieldId}-search`;

  return (
    <fieldset className="min-w-0 border-0 p-0 m-0">
      <legend className="mb-s2 block text-body-sm font-medium text-default">
        Negociação <span className="font-normal text-muted">(opcional)</span>
      </legend>

      {selectedLeadName ? (
        <div
          className={cn(
            'flex items-center gap-s2 rounded-lg border border-border bg-card',
            'px-s4 py-s3 transition-base',
          )}
        >
          <span className="flex-1 min-w-0 truncate text-body-sm text-default">{selectedLeadName}</span>
          <button
            type="button"
            onClick={onClearLead}
            className={cn(
              'shrink-0 rounded-md p-s2 text-secondary',
              'hover:bg-surface-subtle hover:text-default',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'transition-base',
            )}
            aria-label="Remover negociação"
          >
            <XIcon className="size-4" aria-hidden />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Input
            id={searchInputId}
            type="text"
            value={leadSearchTerm}
            onChange={e => onSearchChange(e.target.value)}
            onFocus={onSearchFocus}
            placeholder={isLoadingLeads ? 'Carregando...' : 'Buscar negociação...'}
            icon={<SearchIcon className="size-4 text-muted" aria-hidden />}
            disabled={isLoadingLeads}
            aria-controls={showLeadList ? listboxId : showLeadEmpty ? emptyId : undefined}
            aria-expanded={showLeadList || showLeadEmpty}
            aria-autocomplete="list"
            role="combobox"
          />
          {showLeadList && filteredLeads.length > 0 && (
            <ul
              id={listboxId}
              role="listbox"
              className={cn(
                'absolute top-full left-0 right-0 z-dropdown mt-s1 max-h-48 overflow-y-auto',
                'rounded-lg border border-border bg-background shadow-lg',
              )}
            >
              {filteredLeads.map(option => (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    onClick={() => onSelectLead(option)}
                    className={cn(
                      'w-full px-s4 py-s3 text-left text-body-sm text-secondary transition-base',
                      'hover:bg-primary-subtle hover:text-primary',
                      'focus-visible:outline-none focus-visible:bg-primary-subtle focus-visible:text-primary',
                    )}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showLeadEmpty && (
            <p
              id={emptyId}
              role="status"
              className={cn(
                'absolute top-full left-0 right-0 z-dropdown mt-s1',
                'rounded-lg border border-border bg-background p-s3 text-body-sm text-muted shadow-lg',
              )}
            >
              Nenhuma negociação encontrada
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}
